import {
  ActionButton,
  DropzoneWrapper,
  HelpButton,
  ModalTitle,
  ModalWrapper,
  NotificationMessage,
  StyledStack,
  StyledText,
  Subtitle,
} from "./BulkProductUploadStyledComponents";
import { Dropzone, Modal } from "common/components/ui";
import React, { FC } from "react";

import { BULK_UPLOAD_CONTENT_MESSAGES } from "components/BulkUpload/BulkUploadContentMessages";
import { BulkProductUploadConstants } from "./BulkProductUploadConstants";
import { ElevioArticleId } from "common/elevio/ElevioId";
import { HeaderElement } from "common/components/ui/shared";
import { getBulkUploadDynamicValues, useBulkProductUpload } from "./useBulkProductUpload";
import { useIntl } from "react-intl";
import { ReplenishmentErrorDisplay } from "../ReplenishmentErrorDisplay";
import { BULK_PRODUCT_UPLOAD_ERROR_MESSAGES } from "./BulkProductUploadErrorMessages";
import { BulkProductUploadReport } from "../BulkProductUploadReport";

interface BulkProductUploadModalProps {
  onClose: () => void;
}

export const BulkProductUploadModal: FC<BulkProductUploadModalProps> = ({ onClose }) => {
  const { formatMessage } = useIntl();
  const {
    templateLink,
    instructionsLink,
    modalTitle,
    fileDetails,
    clearFile,
    handleFileUpload,
    handleImportProducts,
    isUploading,
    isImporting,
    uploadError,
    importErrors,
    shouldShowDownloadReport,
  } = useBulkProductUpload(onClose);

  return (
    <Modal onClose={onClose}>
      <ModalWrapper>
        <ModalTitle displayAs={HeaderElement.h4}>{modalTitle}</ModalTitle>

        <Subtitle>{formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.DOWNLOAD_TEMPLATE, { link: templateLink })}</Subtitle>

        <StyledText>
          {formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.NOTE, {
            supportedNumberOfSKUs: BulkProductUploadConstants.MAX_SKUS,
          })}
        </StyledText>

        <NotificationMessage appearance="WARNING">
          <Subtitle>
            {formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.TEMPLATE_UPDATED, { instructions: instructionsLink })}
          </Subtitle>
        </NotificationMessage>

        <ReplenishmentErrorDisplay
          errors={importErrors}
          errorMessages={BULK_PRODUCT_UPLOAD_ERROR_MESSAGES}
          getDynamicValues={getBulkUploadDynamicValues}
        />

        {shouldShowDownloadReport && (
          <BulkProductUploadReport
            infoMessage={formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.BULK_PRODUCT_UPLOAD_REPORT_INFO)}
            style={{ marginTop: "S4" }}
          />
        )}

        <DropzoneWrapper>
          <Dropzone
            id="bulk-product-upload"
            acceptedFileTypes={["csv"]}
            helpText={formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.FILE_TYPE)}
            onChange={handleFileUpload}
            onFileClear={clearFile}
            value={
              fileDetails?.name
                ? {
                    fileName: fileDetails.name,
                  }
                : undefined
            }
            loading={isUploading}
            errorText={uploadError}
          />
        </DropzoneWrapper>

        <StyledStack direction="HORIZONTAL">
          <HelpButton articleId={ElevioArticleId.StorageInboundsImportProducts}>
            {formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.GET_HELP)}
          </HelpButton>
          <ActionButton
            onClick={handleImportProducts}
            disabled={isUploading || !fileDetails || isImporting}
            loading={isImporting}
          >
            {formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.IMPORT_PRODUCTS)}
          </ActionButton>
        </StyledStack>
      </ModalWrapper>
    </Modal>
  );
};
